import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Container, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

import { useGetResourceByIdQuery } from './resourcesApi';

import { asFileContent, type FileResourceContent } from '@/shared/types/resource';
import { resolveSignedUrl, type StorageBucket } from '@/shared/services/storageService';
import { PageSpinner } from '@/shared/components/PageSpinner';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FileResourceViewPage: React.FC = () => {
  const { resourceId } = useParams<{ resourceId: string }>();
  const {
    data: resource,
    isLoading,
    error,
  } = useGetResourceByIdQuery(resourceId!, { skip: !resourceId });

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);

  const fileContent: FileResourceContent | null = resource ? asFileContent(resource) : null;
  const isImage = fileContent?.mimeType?.startsWith('image/') ?? false;
  const isPdf = fileContent?.mimeType === 'application/pdf';

  useEffect(() => {
    if (!fileContent?.storagePath) return;
    let cancelled = false;
    setResolving(true);
    setUrlError(null);
    setPreviewUrl(null);
    setDownloadUrl(null);

    Promise.all([
      resolveSignedUrl(fileContent.storageBucket as StorageBucket, fileContent.storagePath, 3600),
      resolveSignedUrl(fileContent.storageBucket as StorageBucket, fileContent.storagePath, 3600, {
        download: fileContent.originalFilename || true,
      }),
    ])
      .then(([nextPreviewUrl, nextDownloadUrl]) => {
        if (cancelled) return;
        setPreviewUrl(nextPreviewUrl);
        setDownloadUrl(nextDownloadUrl);
      })
      .catch((err) => {
        if (!cancelled) setUrlError(err instanceof Error ? err.message : 'Failed to load file');
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileContent?.originalFilename, fileContent?.storageBucket, fileContent?.storagePath]);

  if (isLoading) return <PageSpinner />;

  if (error || !resource) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Resource not found or you don't have permission to view it.</Alert>
      </Container>
    );
  }

  if (!fileContent || !fileContent.storagePath) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">This resource does not have a file attached.</Alert>
      </Container>
    );
  }

  const handleDownload = () => {
    if (!downloadUrl) return;
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        component={Link}
        to={-1 as unknown as string}
        onClick={(e) => {
          e.preventDefault();
          window.history.back();
        }}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Typography variant="h5" sx={{ mb: 3 }}>
        {resource.title}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {fileContent.originalFilename}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {fileContent.mimeType} &middot; {formatFileSize(fileContent.fileSize)}
              </Typography>
            </Box>
          </Box>

          {urlError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {urlError}
            </Alert>
          )}

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={!downloadUrl || resolving}
          >
            {resolving ? 'Loading...' : 'Download'}
          </Button>
        </CardContent>
      </Card>

      {/* Preview area */}
      {previewUrl && isImage && (
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <img
              src={previewUrl}
              alt={fileContent.originalFilename}
              style={{ maxWidth: '100%', maxHeight: 600, objectFit: 'contain' }}
            />
          </CardContent>
        </Card>
      )}

      {previewUrl && isPdf && (
        <Card>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <iframe
              src={previewUrl}
              title={fileContent.originalFilename}
              style={{ width: '100%', height: 700, border: 'none' }}
            />
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default FileResourceViewPage;
